import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRightIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { formatDisplayDateTime } from '../../../utils/dateFormatting';
import Placeholder from '../../../ui/atoms/Placeholder';
import DashboardWidgetShell from './DashboardWidgetShell';
import { widgetFooterLinkClass } from './widgetAnalyticsUi';

const STATUS_STYLES = {
  draft: 'bg-gray-100 text-gray-700',
  new: 'bg-blue-100 text-blue-800',
  contacted: 'bg-yellow-100 text-yellow-800',
  qualified: 'bg-green-100 text-green-800',
  converted: 'bg-purple-100 text-purple-800',
  lost: 'bg-red-100 text-red-800'
};

const getLeadName = (lead) => {
  const name = [lead.lead_first_name, lead.lead_last_name].filter(Boolean).join(' ').trim();
  return name || lead.lead_email || '-';
};

const RecentLeadsWidget = ({
  recentLeads = [],
  totalLeads = 0,
  onNavigate
}) => {
  const { t, i18n } = useTranslation();

  const subtitle = totalLeads > 0
    ? t('dashboard.agent.leadsCountWeek', { count: totalLeads })
    : t('dashboard.agent.leadsSubtitle');

  return (
    <DashboardWidgetShell
      variant="chart"
      title={t('dashboard.agent.leadsTitle')}
      subtitle={subtitle}
      footer={
        <button
          type="button"
          onClick={() => onNavigate('/agent/ai-agents/leads')}
          className={widgetFooterLinkClass}
        >
          {t('dashboard.agent.viewLeads')}
          <ArrowRightIcon className="ml-1 h-3.5 w-3.5" />
        </button>
      }
    >
      {recentLeads.length > 0 ? (
        <ul className="max-h-64 space-y-1 overflow-y-auto">
          {recentLeads.map((lead) => {
            const statusStyle = STATUS_STYLES[lead.status] || STATUS_STYLES.draft;
            return (
              <li key={lead.id}>
                <button
                  type="button"
                  onClick={() => onNavigate(`/agent/ai-agents/leads/${lead.id}`)}
                  className="w-full rounded-lg px-2 py-2.5 text-left transition-colors hover:bg-gray-50"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {getLeadName(lead)}
                      </p>
                      {lead.lead_email && (
                        <p className="text-xs text-gray-500 truncate mt-0.5">
                          {lead.lead_email}
                        </p>
                      )}
                      <p className="text-[11px] text-gray-500 mt-1">
                        {formatDisplayDateTime(lead.dateCreated, i18n.language)}
                      </p>
                    </div>
                    {lead.status && (
                      <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${statusStyle}`}>
                        {t(`crm.status.${lead.status}`, { defaultValue: lead.status })}
                      </span>
                    )}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <Placeholder
          empty
          size="s"
          icon={UserGroupIcon}
          message={t('dashboard.agent.leadsEmpty')}
        />
      )}
    </DashboardWidgetShell>
  );
};

export default RecentLeadsWidget;
